import type { UpgradePlanName } from './plan-pricing';
import { normalizePlanName, quotaConfig, type PlanType } from './quota-config';

export interface FeatureGateChip {
  label: string;
  // `dot` follows the chip's highlight; `accent-dot` is always the brand colour.
  marker?: 'dot' | 'accent-dot' | 'file';
  highlighted?: boolean;
}

// What the "Today" and "After upgrade" mini previews show.
export type FeatureGatePreview =
  | { type: 'chips'; chips: FeatureGateChip[]; placement?: 'start' | 'bottom-end' }
  | { type: 'meter'; used: number; limit: number; unit: string };

export interface FeatureGateContent {
  plan: UpgradePlanName;
  title: string;
  description: string;
  before: FeatureGatePreview;
  after: FeatureGatePreview;
  benefitsHeading: string;
  benefits: string[];
}

export type FeatureGateId =
  | 'customDomain'
  | 'removeBranding'
  | 'customEmail'
  | 'pdfExport'
  | 'pptxExport'
  | 'longerDocuments';

interface FeatureGate extends Omit<FeatureGateContent, 'benefitsHeading' | 'benefits'> {
  id: FeatureGateId;
  // Listed first in the plan's benefits.
  benefit: string;
}

const formatCount = (value: number) => value.toLocaleString('en-US');
const credits = (plan: PlanType) =>
  `${formatCount(quotaConfig[plan].monthlyAISections)} AI credits/mo`;
const sources = (plan: PlanType) => `${quotaConfig[plan].contentLibrarySources} content sources`;
const FREE_PAGE_LIMIT = quotaConfig.free.aiGenerationMaxPages ?? 0;

export const FEATURE_GATE_PLANS: {
  plan: UpgradePlanName;
  benefitsHeading: string;
  benefits: string[];
}[] = [
  {
    plan: 'Starter',
    benefitsHeading: 'Everything in Free, plus',
    benefits: [
      'PDF export',
      credits('Starter'),
      sources('Starter'),
      'Longer documents',
      'E-signatures',
      'All document types',
    ],
  },
  {
    plan: 'Professional',
    benefitsHeading: 'Everything in Starter, plus',
    benefits: [
      'Custom domain',
      'Remove branding',
      'Custom email',
      'PPT export',
      credits('Professional'),
      sources('Professional'),
    ],
  },
  {
    plan: 'Business',
    benefitsHeading: 'Everything in Professional, plus',
    benefits: [
      sources('Business'),
      credits('Business'),
      `${quotaConfig.Business.usersPerWorkspace} team seats`,
    ],
  },
];

export const FEATURE_GATES: FeatureGate[] = [
  {
    id: 'customDomain',
    plan: 'Professional',
    benefit: 'Custom domain',
    title: 'Share proposals from your own domain',
    description: 'Send clients a link on your own URL instead of a proposal.biz address.',
    before: { type: 'chips', chips: [{ label: 'proposal.biz/p/acme-q4' }] },
    after: { type: 'chips', chips: [{ label: 'yourcompany.com', highlighted: true }] },
  },
  {
    id: 'removeBranding',
    plan: 'Professional',
    benefit: 'Remove branding',
    title: 'Send proposals that look entirely yours',
    description:
      'Remove the “Made with proposal.biz” badge from shared links, published viewers and exported PDFs.',
    before: {
      type: 'chips',
      placement: 'bottom-end',
      chips: [{ label: 'Made with proposal.biz', marker: 'accent-dot' }],
    },
    after: { type: 'chips', chips: [] },
  },
  {
    id: 'customEmail',
    plan: 'Professional',
    benefit: 'Custom email',
    title: 'Send documents from your own email',
    description: 'Clients see your name and domain, not ours.',
    before: { type: 'chips', chips: [{ label: 'noreply@proposal.biz' }] },
    after: { type: 'chips', chips: [{ label: 'you@yourcompany.com', highlighted: true }] },
  },
  {
    id: 'pdfExport',
    plan: 'Starter',
    benefit: 'PDF export',
    title: 'Send a polished PDF',
    description: 'Download any proposal as a PDF to email, print or attach to a contract.',
    before: { type: 'chips', chips: [{ label: 'Share link only', marker: 'dot' }] },
    after: {
      type: 'chips',
      chips: [
        { label: 'Link', marker: 'dot' },
        { label: 'Proposal.pdf', marker: 'file', highlighted: true },
      ],
    },
  },
  {
    id: 'pptxExport',
    plan: 'Professional',
    benefit: 'PPT export',
    title: 'Present it in PowerPoint',
    description: 'Export your proposal as an editable PPT for client meetings and pitches.',
    before: { type: 'chips', chips: [{ label: 'PDF only', marker: 'dot' }] },
    after: {
      type: 'chips',
      chips: [
        { label: 'PDF', marker: 'dot' },
        { label: 'Proposal.pptx', marker: 'dot', highlighted: true },
      ],
    },
  },
  {
    id: 'longerDocuments',
    plan: 'Starter',
    benefit: 'Longer documents',
    title: `We created the first ${FREE_PAGE_LIMIT} pages`,
    description: `Free creates up to ${FREE_PAGE_LIMIT} pages per document. Upgrade to create longer documents with AI.`,
    before: { type: 'meter', used: FREE_PAGE_LIMIT, limit: FREE_PAGE_LIMIT, unit: 'pages' },
    after: { type: 'chips', chips: [{ label: 'No page limit', highlighted: true }] },
  },
];

function planBenefits(plan: UpgradePlanName, first: string) {
  const config = FEATURE_GATE_PLANS.find((entry) => entry.plan === plan);
  if (!config) return { benefitsHeading: '', benefits: [] };
  return {
    benefitsHeading: config.benefitsHeading,
    benefits: [first, ...config.benefits.filter((benefit) => benefit !== first)],
  };
}

export function getFeatureGate(id: FeatureGateId): FeatureGateContent {
  const gate = FEATURE_GATES.find((entry) => entry.id === id);
  if (!gate) throw new Error(`Unknown feature gate: ${id}`);
  const { id: _id, benefit, ...content } = gate;
  return { ...content, ...planBenefits(gate.plan, benefit) };
}

// The prompt before generating a file past the Free page limit.
export function getUploadPageLimitGate(
  sourcePages: number,
  isFaithfulImport: boolean,
): FeatureGateContent {
  const limitNote = `Free creates up to ${FREE_PAGE_LIMIT} pages per document`;
  return {
    plan: 'Starter',
    title: `This file has ${sourcePages} pages`,
    description: isFaithfulImport
      ? `${limitNote}, so we'll import the first ${FREE_PAGE_LIMIT}. Upgrade to import all ${sourcePages}.`
      : `${limitNote}, so we'll condense it to ${FREE_PAGE_LIMIT}. Upgrade to keep the full length.`,
    before: { type: 'meter', used: FREE_PAGE_LIMIT, limit: FREE_PAGE_LIMIT, unit: 'pages' },
    after: { type: 'chips', chips: [{ label: 'No page limit', highlighted: true }] },
    ...planBenefits('Starter', 'Longer documents'),
  };
}

const UPGRADE_ORDER: UpgradePlanName[] = ['Starter', 'Professional', 'Business'];

// The plan one step above the current one: free → Starter → Professional → Business.
export function getNextUpgradePlan(planName: string | null | undefined): UpgradePlanName | null {
  const current = normalizePlanName(planName);
  if (current === 'free') return 'Starter';
  const index = UPGRADE_ORDER.indexOf(current as UpgradePlanName);
  return index === -1 ? null : (UPGRADE_ORDER[index + 1] ?? null);
}

// The next plan up that raises the content source limit; null when there is none to sell.
export function getContentSourcesGate(
  planName: string | null | undefined,
  used: number,
): FeatureGateContent | null {
  const current = normalizePlanName(planName);
  const limit = quotaConfig[current].contentLibrarySources;
  const next = UPGRADE_ORDER.find((plan) => {
    const nextLimit = quotaConfig[plan].contentLibrarySources;
    return limit !== null && (nextLimit === null || nextLimit > limit);
  });
  const nextLimit = next ? quotaConfig[next].contentLibrarySources : null;
  if (!next || limit === null || nextLimit === null) return null;

  const currentLabel = current === 'free' ? 'free' : current;
  return {
    plan: next,
    title: 'Connect more data sources',
    description: `You’ve used all ${limit} ${currentLabel} sources. ${next} lets you connect up to ${nextLimit}.`,
    before: { type: 'meter', used, limit, unit: 'sources' },
    after: { type: 'meter', used, limit: nextLimit, unit: 'sources' },
    ...planBenefits(next, sources(next)),
  };
}
